import Header from 'components/header/Header'
import { FC, useState, useEffect } from 'react'
import Head from 'next/head'
import { useTranslation } from 'next-i18next'
import { LayoutProps as Props } from './types'
import styles from './Layout.module.scss'
import Footer from 'components/footer/Footer'
import Banner from 'components/banner/Banner'
import { useDispatch, useSelector } from 'react-redux'
import { showSimulation } from 'store/simulation/actions/showSimulation'
import { trackEvent } from 'utils/eventTracking'
import SimulationPopup from 'features/simulation/components/simulation-popup/SimulationPopup'
import { TITLE_POSTFIX } from 'constants/seo'
import { Store } from 'store/types'
import { useRouter } from 'next/router'
import { routes } from 'utils/routes'
import Button from 'components/button/Button'
import { resetSimulationStore } from 'store/simulation/actions/resetSimulationStore'
import { resetRegistrationStore } from 'store/registration/actions/resetRegistrationStore'
import classNames from 'classnames'

const Layout: FC<Props> = ({ isHome, hideHeader, children }) => {
  // REDUX STORE
  const { marketing } = useSelector((store: Store) => store)
  const { didSimulation } = useSelector((store: Store) => store.simulation)
  const dispatch = useDispatch()

  // i18n
  const { t } = useTranslation(['home', 'common'])

  // Next Router
  const router = useRouter()
  const { locale } = router
  // State
  const [showCookieBanner, setShowCookieBanner] = useState<boolean>(false)

  useEffect(() => {
    setShowCookieBanner(window && window.location.hostname.includes('boltenergie.be'))
  }, [])

  /**
   * Handles the banner on click event
   * Shows simulation or redirects to producer page if simulation has been done
   */
  const handleOnBannerClick = () => {
    if (didSimulation) {
      router.push(routes(locale).producers)
    } else {
      dispatch(showSimulation())

      // Track 'Simulation Started' event @Semetis
      trackEvent('simulation_started')
    }
  }

  /**
   * Handles the reset button onClick event
   */
  const handleOnResetButtonClick = () => {
    dispatch(resetSimulationStore())
    dispatch(resetRegistrationStore())
    router.push(routes(locale).home)
  }

  return (
    <>
      {/* Default SEO */}
      <Head>
        <title>{t('seoTitle')}</title>
        <title>
          {t('seoTitle')} {TITLE_POSTFIX}
        </title>
        <meta property="og:title" content={`${t('seoTitle')} ${TITLE_POSTFIX}`} key="title" />

        {showCookieBanner && (
          <script
            id="Cookiebot"
            data-culture={locale.toUpperCase()}
            src="https://consent.cookiebot.com/uc.js"
            data-cbid="b9b9c00c-7571-4ef2-aec7-da61c9cda61e"
            data-blockingmode="auto"
            type="text/javascript"
            async
          />
        )}
      </Head>

      <div className={styles.layout}>
        {!hideHeader && <Header isHome={isHome} />}

        <Banner onClickHandler={handleOnBannerClick} />

        <SimulationPopup />

        <div className={classNames({ [styles['with-banner']]: !!(marketing.banner || marketing.bannerType != '') })}>{children}</div>

        {marketing.resetButton && (
          <Button extraClasses={styles['reset-button']} onClick={() => handleOnResetButtonClick()}>
            {t('common:resetButton', 'Reset')}
          </Button>
        )}

        <Footer />
      </div>
    </>
  )
}

Layout.defaultProps = {
  isHome: false,
  hideHeader: false
}

export default Layout
